/**
 * Единый источник пунктов меню клиники и логики доступа/активного маршрута.
 * Используется в Personal/Menu.vue и Header/Menu.vue.
 */

import {isShopEnabled} from '~/composables/useShopFeature';
import {isClinicPreviewPathActiveForMenuLink} from '~/utils/personalSectionPreview';
import {omitShopMenuItems} from '~/utils/shopFeature';

export interface ClinicMenuItem {
  link: string;
  name: string;
  icon?: string;
  separatorAfter?: boolean;
  requiresClinicVerification?: boolean;
  requiresClinicOnline?: boolean;
}

export type ComputedClinicMenuItem = ClinicMenuItem & {
  isActive: boolean;
  isDisabled: boolean;
  disableReason: string;
};

/**
 * Возвращает актуальный список пунктов меню клиники.
 */
export function getClinicMenuItems(): ClinicMenuItem[] {
  return omitShopMenuItems([
    {link: '/profile-clinic/doctors/', name: 'Врачи', icon: 'doctors'},
    {link: '/profile-clinic/doctors/schedule/', name: 'Расписание', icon: 'schedule'},
    {link: '/profile-clinic/services/', name: 'Услуги', icon: 'services'},
    {link: '/profile-clinic/showcase/', name: 'Витрина', icon: 'showcase'},
    {link: '/profile-clinic/orders/', name: 'Заказы', icon: 'orders'},
    {
      link: '/profile-clinic/online-consultations/',
      name: 'Онлайн-консультации',
      icon: 'online-consultations',
    },
    {
      link: '/profile-clinic/applications/',
      name: 'Заявки',
      icon: 'applications',
      separatorAfter: true,
    },
    {link: '/profile-clinic/finances/', name: 'Финансы', icon: 'finances', requiresClinicOnline: true},
    {link: '/profile-clinic/reviews/', name: 'Отзывы', icon: 'reviews', separatorAfter: true},
    {link: '/profile-clinic/widget/', name: 'Виджет для сайта', icon: 'widget'},
    {link: '/profile-clinic/education/', name: 'Обучение', icon: 'education', separatorAfter: true},
    {
      link: '/profile-clinic/integration/',
      name: 'Интеграции',
      icon: 'integrations',
      separatorAfter: true,
    },
    {link: '/profile-clinic/edit/', name: 'Профиль', icon: 'profile'},
    {link: '/profile-clinic/requisites/', name: 'Реквизиты и документы', icon: 'requisites'},
    {link: '/profile-clinic/notifications/', name: 'Настройка уведомлений', icon: 'notifications'},
  ], isShopEnabled());
}

const ROUTE_ALIASES_BY_LINK: Record<string, RegExp[]> = {
  '/profile-clinic/doctors/': [/^\/profile-clinic\/doctors\/new\/?$/],
  '/profile-clinic/doctors/schedule/': [/^\/profile-clinic\/doctors\/\d+\/schedule\/?$/],
  '/profile-clinic/online-consultations/': [
    /^\/profile-clinic\/appointment-\d+\/?$/,
    /^\/profile-clinic\/history\/?$/,
    /^\/profile-clinic\/doctors\/history\/?$/,
  ],
  '/profile-clinic/applications/': [
    /^\/profile-clinic\/applications\/(booking|submission)-\d+\/?$/,
  ],
};

export function normalizeMenuPath(path: string): string {
  return path.endsWith('/') ? path : `${path}/`;
}

function isRouteMatchedByAlias(path: string, link: string): boolean {
  const aliases = ROUTE_ALIASES_BY_LINK[link];
  if (!aliases?.length) return false;
  return aliases.some((pattern) => pattern.test(path));
}

/**
 * Активный пункт меню клиники: префикс URL + алиасы (в т.ч. узкий кейс «Врачи»).
 */
export function isClinicMenuItemActive(routePath: string, item: Pick<ClinicMenuItem, 'link'>): boolean {
  if (isClinicPreviewPathActiveForMenuLink(routePath, item.link)) {
    return true;
  }
  const path = normalizeMenuPath(routePath);
  const link = normalizeMenuPath(item.link);
  const isPrefixMatch = path.startsWith(link);

  if (item.link === '/profile-clinic/doctors/') {
    return path === link || isRouteMatchedByAlias(path, item.link);
  }

  return isPrefixMatch || isRouteMatchedByAlias(path, item.link);
}

export type ClinicMenuGates = {
  verified: boolean;
  onlineReady: boolean;
};

export function isClinicMenuItemDisabled(item: ClinicMenuItem, gates: ClinicMenuGates): boolean {
  if (item.requiresClinicVerification === true && !gates.verified) {
    return true;
  }
  return item.requiresClinicOnline === true && !gates.onlineReady;
}

export function getClinicMenuDisableReason(item: ClinicMenuItem, gates: ClinicMenuGates): string {
  if (item.requiresClinicOnline === true) {
    if (!gates.verified) {
      return 'Загрузите документы в разделе «Реквизиты и документы», чтобы открыть доступ к разделу.';
    }
    if (!gates.onlineReady) {
      return 'Для доступа настройте онлайн-приём\n1. В разделе «Реквизиты и документы» заполните платежные данные.\n2. В разделе «Онлайн-консультации» включите "Принимать онлайн".\nРазделы активируются сразу после проверки информации.';
    }
  }
  if (item.requiresClinicVerification === true && !gates.verified) {
    return 'Загрузите документы в разделе «Реквизиты и документы», чтобы открыть доступ к разделу.';
  }
  return '';
}

export function getClinicMenuBlockedModalTitle(item: ClinicMenuItem): string {
  if (item.requiresClinicOnline) {
    return 'Для доступа настройте онлайн-приём';
  }
  return 'Требуется подтверждение клиники';
}

export function useClinicProfileGates() {
  const {type} = usePersonalType();
  const authStore = useAuthStore();
  const {userProfileData} = storeToRefs(authStore);

  const isClinicVerifiedForAccess = computed(() => {
    if (type.value !== 'clinic') return true;
    return (
      userProfileData.value?.status === 'publish'
      && userProfileData.value?.isDocumentsVerified === true
    );
  });

  const isClinicOnlineReady = computed(() => {
    if (type.value !== 'clinic') return true;
    return (
      isClinicVerifiedForAccess.value
      && userProfileData.value?.isOnlineEnabled === true
      && userProfileData.value?.isRequisitesFilled === true
      && userProfileData.value?.canEnableOnline === true
    );
  });

  const gates = computed(
    (): ClinicMenuGates => ({
      verified: isClinicVerifiedForAccess.value,
      onlineReady: isClinicOnlineReady.value,
    }),
  );

  return {
    isClinicVerifiedForAccess,
    isClinicOnlineReady,
    gates,
  };
}

/**
 * Готовые пункты меню клиники с isActive / isDisabled / disableReason для шапки и др.
 */
export function useClinicMenuComputedItems() {
  const route = useRoute();
  const {gates} = useClinicProfileGates();

  return computed((): ComputedClinicMenuItem[] => {
    const g = gates.value;
    return getClinicMenuItems().map((item) => ({
      ...item,
      isActive: isClinicMenuItemActive(route.path, item),
      isDisabled: isClinicMenuItemDisabled(item, g),
      disableReason: getClinicMenuDisableReason(item, g),
    }));
  });
}
